import { Core, Decorative } from "@faire/design-tokens";
import { AutocompleteAddressInput } from "@faire/retailer-visitor-shared/components/AutocompleteAddressInput/AutocompleteAddressInput";
import { AddressType } from "@faire/retailer-visitor-shared/components/AutocompleteAddressInput/AutocompleteAddressInputState";
import { AdditionalInfoUSDateInput } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/AdditionalInfoUSDateInput";
import {
  HighlightErrorFieldKeys,
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts";
import { CreditCheckAuthorizationSection } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/CreditCheckAuthorizationSection";
import { RootNestedCheckbox } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/NestedCheckboxes/index";
import { INestedCheckboxOption } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/NestedCheckboxes/NestedCheckboxes";
import { OwFFileUploader } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFileUploader";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useOwFIDVTierLogic } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFIDVTierLogic";
import { displayableMoneyRangeToOption } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/utils";
import { SimplifiedAddressForm } from "@faire/retailer-visitor-shared/components/SimplifiedAddressForm/SimplifiedAddressForm";
import { useRetailerFinancial } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerFinancial";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Checkbox } from "@faire/web--source/slate/Checkbox";
import { Dropdown, IOption } from "@faire/web--source/slate/Dropdown";
import { Search } from "@faire/web--source/slate/icons/foundation/Search";
import { Input } from "@faire/web--source/slate/Input";
import { MoneyInput } from "@faire/web--source/slate/Input/MoneyInput";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Column } from "@faire/web--source/slate/Layout";
import { Radio } from "@faire/web--source/slate/Radio";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IDisplayableMoneyRange } from "@faire/web-api--source/indigofair/data/IDisplayableMoneyRange";
import { IDocument } from "@faire/web-api--source/indigofair/data/IDocument";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import React from "react";
import styled from "styled-components";

export enum EXPLANATION_POSITION {
  TOP = "TOP",
  BOTTOM = "BOTTOM",
}

export type OwFFormFieldValueType =
  | string
  | number
  | boolean
  | Partial<IAddress>
  | IDocument[]
  | IFaireMoney
  | undefined;

export interface IOwFFormFieldSchema {
  fieldType: OwFFormFieldType;
  fieldName: OwFFormFieldKey;
  required?: boolean;
  shouldNotShowOptional?: boolean;
  // Label and explanation
  labelText?: string;
  explanation?: string;
  explanationPosition?: keyof typeof EXPLANATION_POSITION;
  subTitleList?: string[];
  placeholder?: string;
  tooltipText?: string;
  // Input prefix and suffix
  inputPrefix?: React.ReactNode;
  inputSuffix?: React.ReactNode;
  // maxLength for input elements
  maxLength?: number;
  // Radio and dropdown options
  options?: IOption[];
  // Nested checkbox options
  nestedOptions?: INestedCheckboxOption[];
  // note appearing under a field
  note?: string;
  // helper text appearing under a dropdown selector
  helper?: string;
  // Checkbox items
  checkBoxValues?: Partial<Checkbox.IProps>[];
  // Displayable money range options
  moneyRangeOptions?: IDisplayableMoneyRange[];
  onChange?: (newValue: OwFFormFieldValueType) => void;
  onSelectAddress?: (placeId: string) => void;
  toggleAddressForm?: () => void;
  isWrappedField?: boolean;
  fullWidth?: boolean;
  hideCharacterCount?: boolean;
}

interface IProps {
  onChange: (newValue: OwFFormFieldValueType) => void;
  onChangeRange?: (rangeIndexStr: string) => void;
  eventPathBase?: string;
  value?: OwFFormFieldValueType;
  error?: string;
  isWholeSectionOptional?: boolean;
}

export const OwFFormField: React.FC<IOwFFormFieldSchema & IProps> = (props) => {
  const {
    labelText,
    explanation,
    explanationPosition = EXPLANATION_POSITION.TOP,
    subTitleList,
    note,
    required,
    shouldNotShowOptional,
    tooltipText,
    isWholeSectionOptional,
    fieldName,
  } = props;

  const { isAnyTierActive } = useOwFIDVTierLogic();

  if (!labelText) {
    return <FormInput {...props} />;
  }
  return (
    <Column>
      <FieldLabel
        labelText={labelText}
        required={required}
        shouldNotShowOptional={shouldNotShowOptional}
        isWholeSectionOptional={isWholeSectionOptional}
        tooltipText={tooltipText}
        fieldName={fieldName}
        error={
          HighlightErrorFieldKeys.includes(props.fieldName) &&
          isNotUndefined(props.error) &&
          isAnyTierActive
        }
      />
      {(subTitleList?.length ?? 0) > 0 ? (
        <SubtitleList>
          {subTitleList?.map((item: string) => (
            <SubtitleListItem key={item}>
              <Span>{item}</Span>
            </SubtitleListItem>
          ))}
        </SubtitleList>
      ) : null}
      {explanation && explanationPosition === EXPLANATION_POSITION.TOP ? (
        <Typography color={Core.text.subdued}>{explanation}</Typography>
      ) : null}
      <Spacer height="1x" />
      <FormInput {...props} />
      {explanation && explanationPosition === EXPLANATION_POSITION.BOTTOM ? (
        <Typography variant="labelSansRegular" color={Core.text.subdued}>
          {explanation}
        </Typography>
      ) : null}
      <Spacer height="3x" />
      {note ? (
        <NoteWrapper>
          <Typography>{note}</Typography>
        </NoteWrapper>
      ) : null}
    </Column>
  );
};

type IFieldLabelProps = Pick<
  IOwFFormFieldSchema,
  | "labelText"
  | "required"
  | "shouldNotShowOptional"
  | "tooltipText"
  | "fieldName"
> &
  Pick<IProps, "isWholeSectionOptional"> & { error?: boolean };
const FieldLabel: React.FC<IFieldLabelProps> = ({
  labelText,
  required,
  shouldNotShowOptional,
  tooltipText,
  isWholeSectionOptional,
  fieldName,
  error,
}) => {
  if (tooltipText) {
    return (
      <div>
        <InlineTypography>
          {labelText}
          <ConditionalOptionalText
            required={required}
            isWholeSectionOptional={isWholeSectionOptional}
            shouldNotShowOptional={shouldNotShowOptional}
          />
        </InlineTypography>
        &nbsp;
        <AlignedTooltip contents={[{ body: tooltipText }]} position="top" />
      </div>
    );
  }

  return (
    <Typography
      color={error ? Core.text.critical : undefined}
      as="label"
      htmlFor={`input-${fieldName}`}
    >
      {labelText}
      <ConditionalOptionalText
        required={required}
        isWholeSectionOptional={isWholeSectionOptional}
        shouldNotShowOptional={shouldNotShowOptional}
      />
    </Typography>
  );
};

type IConditionalOptionalText = Pick<
  IFieldLabelProps,
  "required" | "isWholeSectionOptional" | "shouldNotShowOptional"
>;
const ConditionalOptionalText: React.FC<IConditionalOptionalText> = ({
  required,
  isWholeSectionOptional,
  shouldNotShowOptional,
}) => {
  if (required || isWholeSectionOptional || shouldNotShowOptional) {
    return null;
  }

  return (
    <>
      {" "}
      <Span color={Core.text.subdued}>
        <StrictLocalMsg
          defaultMessage="(optional)"
          description={{
            text: "Additional text stating that the option is optional.",
            img: "https://cdn.faire.com/fastly/f9bc0e0f8ecff7cac36909ed11a28d38c8b197ee68524b84e601cb11287e7252.png",
          }}
        />
      </Span>
    </>
  );
};

const FormInput: React.FC<IOwFFormFieldSchema & IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { currency } = useRetailerFinancial();
  const { isNMIApplication } = useIsNMIApplication();
  const characterCount = props.maxLength ?? 255;
  const selectAResponsePlaceholder =
    props.placeholder ||
    strictLocalize({
      defaultMessage: "Select a response",
      description: { text: "Placeholder in a dropdown" },
    });

  const handleOnTextChange = (value: string) => {
    if (value.length > characterCount) {
      props.onChange(value.slice(0, characterCount));
    } else {
      props.onChange(value);
    }
  };

  return {
    [OwFFormFieldType.INPUT]: (
      <Input
        data-test-id={props.fieldName}
        id={`input-${props.fieldName}`}
        required={props.required}
        onChange={(e) => handleOnTextChange(e.target.value)}
        name={props.fieldName}
        value={props.value?.toString() || ""}
        error={props.error}
      />
    ),
    [OwFFormFieldType.NUMBER]: (
      <Input
        data-test-id={props.fieldName}
        id={`input-${props.fieldName}`}
        required={props.required}
        onChange={(e) => handleOnTextChange(e.target.value)}
        name={props.fieldName}
        type="number"
        prefixNode={props.inputPrefix}
        suffixNode={props.inputSuffix}
        value={props.value?.toString() || ""}
        error={props.error}
      />
    ),
    [OwFFormFieldType.DROPDOWN]: (
      <Dropdown
        data-test-id={props.fieldName}
        id={`input-${props.fieldName}`}
        options={props.options ?? []}
        onSelect={({ value }) => props.onChange(value)}
        placeholder={selectAResponsePlaceholder}
        value={props.value?.toString() || ""}
        error={props.error}
        helper={props.helper}
        disableInput
      />
    ),
    [OwFFormFieldType.MONEY_RANGE_DROPDOWN]: (
      <Dropdown
        data-test-id={props.fieldName}
        id={`input-${props.fieldName}`}
        options={(props.moneyRangeOptions ?? []).map(
          displayableMoneyRangeToOption
        )}
        onSelect={({ value }) =>
          (props.onChangeRange ?? props.onChange)?.(value)
        }
        placeholder={selectAResponsePlaceholder}
        value={props.value?.toString() || ""}
        error={props.error}
        helper={props.helper}
        disableInput
      />
    ),
    [OwFFormFieldType.RADIO]: (
      <Column>
        <Spacer height={2} />
        {props.options?.map(({ label, value }, index: number) => (
          <React.Fragment key={`${props.fieldName}-option-${index}`}>
            <Radio
              label={label}
              active={props.value === value}
              onClick={() => props.onChange(value)}
              data-test-id={`${props.fieldName}-${value}`}
            />
            {index < (props.options?.length ?? 0) - 1 ? (
              <Spacer height={12} />
            ) : null}
          </React.Fragment>
        ))}
        {props.error ? (
          <>
            <Spacer height="1x" />
            <Typography color={Core.text.critical}>
              <StrictLocalMsg
                defaultMessage="Required"
                description={{
                  text: "Indicate the field in a form is required",
                }}
              />
            </Typography>
          </>
        ) : null}
      </Column>
    ),
    [OwFFormFieldType.RESIDENTIAL_ADDRESS_AUTOCOMPLETE]: (
      <AutocompleteAddressInput
        value={props.value?.toString() || ""}
        placeholder={props?.placeholder}
        type={AddressType.RESIDENTIAL}
        id={props.fieldName}
        onChange={props.onChange}
        onCannotFindClick={props.toggleAddressForm}
        onSelect={props.onSelectAddress}
        error={props.error}
        prefixNode={isNMIApplication ? <Search /> : undefined}
      />
    ),
    [OwFFormFieldType.STORE_ADDRESS_AUTOCOMPLETE]: (
      <AutocompleteAddressInput
        value={props.value?.toString() || ""}
        placeholder={props?.placeholder}
        type={AddressType.BUSINESS}
        id={props.fieldName}
        onChange={props.onChange}
        onCannotFindClick={props.toggleAddressForm}
        onSelect={props.onSelectAddress}
        error={props.error}
      />
    ),
    [OwFFormFieldType.ADDRESS_FORM]: (
      <SimplifiedAddressForm
        onChange={props.onChange}
        address={props.value as Partial<IAddress>}
        error={props.error}
        prefillCountry
        isOwFApplicationForm
      />
    ),
    [OwFFormFieldType.TEXTAREA]: (
      <TextArea
        data-test-id={props.fieldName}
        id={`input-${props.fieldName}`}
        name={props.fieldName}
        style={{ height: "60px" }}
        value={props.value?.toString() || ""}
        onChange={(e) => handleOnTextChange(e.target.value)}
        error={props.error}
      />
    ),
    [OwFFormFieldType.FILE]: (
      <OwFFileUploader
        value={(props.value ?? []) as IDocument[]}
        onChange={props.onChange}
        fieldName={props.fieldName}
        error={!!props.error}
      />
    ),
    [OwFFormFieldType.MONEY]: (
      <MoneyInput
        data-test-id={props.fieldName}
        id={`input-${props.fieldName}`}
        onChange={(amountCents?: number) =>
          props.onChange(
            isNotUndefined(amountCents)
              ? IFaireMoney.build({
                  amount_cents: amountCents,
                  currency,
                })
              : undefined
          )
        }
        currency={currency}
        value={(props.value as IFaireMoney)?.amount_cents}
        error={props.error}
      />
    ),
    [OwFFormFieldType.CHECKBOX]: (
      <Column>
        <Spacer height={2} />
        {props.checkBoxValues?.map(
          ({ label, checked, onChange }, index: number) => (
            <React.Fragment key={`${props.fieldName}-option-${index}`}>
              <Checkbox
                label={label}
                checked={checked ?? false}
                onChange={onChange}
                data-test-id={`${props.fieldName}-${index}`}
              />
              {index < (props.checkBoxValues?.length ?? 0) - 1 ? (
                <Spacer height="1x" />
              ) : null}
            </React.Fragment>
          )
        )}
        {props.error ? (
          <>
            <Spacer height="1x" />
            <Typography color={Core.text.critical}>
              <StrictLocalMsg
                defaultMessage="Required"
                description={{
                  text: "Indicate the field in a form is required",
                }}
              />
            </Typography>
          </>
        ) : null}
      </Column>
    ),
    [OwFFormFieldType.NESTED_CHECKBOXES]: (
      <RootNestedCheckbox
        options={props.nestedOptions ?? []}
        onChange={props.onChange}
        value={props.value?.toString()}
      />
    ),
    [OwFFormFieldType.CREDIT_CHECK_AUTH]: (
      <CreditCheckAuthorizationSection
        eventPathBase={props.eventPathBase}
        checked={!!props.value}
        onChange={props.onChange}
        error={props.error}
        isOpenWithFaire
        isOwFApplicationForm
      />
    ),
    [OwFFormFieldType.US_DATE]: (
      <AdditionalInfoUSDateInput
        value={props.value?.toString()}
        onChange={props.onChange}
        error={props.error}
        name={props.fieldName}
      />
    ),
  }[props.fieldType];
};

const NoteWrapper = styled(Column)`
  margin-top: 12px;
  padding: 20px;
  background-color: ${Decorative.tint.blue};
`;

const SubtitleList = styled.ul`
  list-style-position: inside;
  margin-left: ${getSpacing("2x")};
`;

const SubtitleListItem = styled.li`
  list-style-type: disc;
`;

const AlignedTooltip = styled(Tooltip)`
  vertical-align: text-bottom;
`;

const InlineTypography = styled(Typography)`
  display: inline;
`;
